import { useMemo, useState } from 'react';
import { formatPhone, normalizePhone, PHONE_COUNTRIES, PHONE_INVALID, phoneCountry, type CountryCode } from '@photos/shared';

export interface PhoneValue {
  /** E.164, or null when empty */
  e164: string | null;
  /** false while something is typed that isn't a valid number for the country */
  valid: boolean;
}

const names = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    return null;
  }
})();

/**
 * "Phone (optional)": a country selector (United States +1 by default) and the number as people type it. Reports the
 * number in E.164, or that it isn't valid yet. Not verified; no SMS.
 */
export function PhoneField({ id, initial, onChange }: { id: string; initial: string | null; onChange(v: PhoneValue): void }) {
  const [country, setCountry] = useState<CountryCode>(() => (initial && phoneCountry(initial)) || 'US');
  const [text, setText] = useState(() => (initial ? formatPhone(initial) : ''));
  const countries = useMemo(
    () =>
      PHONE_COUNTRIES.map((c) => ({ ...c, name: names?.of(c.code) ?? c.code })).sort((a, b) =>
        a.code === 'US' ? -1 : b.code === 'US' ? 1 : a.name.localeCompare(b.name),
      ),
    [],
  );
  const e164 = text.trim() ? normalizePhone(text, country) : null;
  const invalid = !!text.trim() && !e164;
  const report = (t: string, c: CountryCode) => {
    const v = t.trim() ? normalizePhone(t, c) : null;
    onChange({ e164: v, valid: !t.trim() || !!v });
  };

  return (
    <>
      <div className="phone">
        <select
          className="inp"
          aria-label="Country"
          value={country}
          onChange={(e) => {
            const c = e.target.value as CountryCode;
            setCountry(c);
            report(text, c);
          }}
        >
          {countries.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name} ({c.dial})
            </option>
          ))}
        </select>
        <input
          id={id}
          className="inp"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="(201) 555-0123"
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? `${id}-err` : undefined}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            report(e.target.value, country);
          }}
        />
      </div>
      {invalid && (
        <small id={`${id}-err`} style={{ color: 'var(--danger)' }}>
          {PHONE_INVALID}
        </small>
      )}
    </>
  );
}
